import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { addDays, format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PageHeader } from '@/components/common';
import {
  maintenanceScheduleApi,
  MAINTENANCE_FREQUENCIES,
  MAINTENANCE_PRIORITIES,
} from '@/api/services/maintenance-schedule.api';
import { equipmentApi } from '@/api/services/equipment.api';
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

export default function MaintenanceSchedulesNew() {
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    equipmentId: '',
    frequency: 'MONTHLY',
    intervalDays: '',
    intervalHours: '',
    nextDue: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
    priority: 'MEDIUM',
    estimatedDuration: '',
    instructions: '',
    checklistItems: '',
  });

  useEffect(() => {
    equipmentApi
      .getAll({ limit: 100 })
      .then((res) => setEquipment(res.data.data))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.equipmentId) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      const data: any = {
        name: formData.name,
        equipmentId: formData.equipmentId,
        frequency: formData.frequency,
        nextDue: formData.nextDue,
        priority: formData.priority,
      };

      if (formData.intervalDays) data.intervalDays = parseInt(formData.intervalDays);
      if (formData.intervalHours) data.intervalHours = parseFloat(formData.intervalHours);
      if (formData.estimatedDuration)
        data.estimatedDuration = parseFloat(formData.estimatedDuration);
      if (formData.instructions) data.instructions = formData.instructions;
      if (formData.checklistItems) {
        data.checklistItems = formData.checklistItems.split('\n').filter(Boolean);
      }

      await maintenanceScheduleApi.create(data);
      toast.success('Maintenance schedule created successfully');
      navigate('/maintenance');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create schedule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6">
      <PageHeader
        title="New Maintenance Schedule"
        description="Details required to set a preventive routine against an asset at a fixed interval."
        actions={
          <Button variant="outline" onClick={() => navigate('/maintenance')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Schedules
          </Button>
        }
      />

      <motion.div variants={fadeUp}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Schedule Details */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Schedule Details</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="name">Schedule Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Monthly Filter Replacement"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="equipment">Equipment *</Label>
              <Select
                value={formData.equipmentId}
                onValueChange={(v) => setFormData({ ...formData, equipmentId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select equipment" />
                </SelectTrigger>
                <SelectContent>
                  {equipment.map((eq) => (
                    <SelectItem key={eq.id} value={eq.id}>
                      {eq.name} ({eq.assetNumber})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="frequency">Frequency</Label>
                <Select
                  value={formData.frequency}
                  onValueChange={(v) => setFormData({ ...formData, frequency: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MAINTENANCE_FREQUENCIES.map((f) => (
                      <SelectItem key={f.value} value={f.value}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(v) => setFormData({ ...formData, priority: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MAINTENANCE_PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="nextDue">Next Due Date</Label>
              <Input
                id="nextDue"
                type="date"
                value={formData.nextDue}
                onChange={(e) => setFormData({ ...formData, nextDue: e.target.value })}
              />
            </div>
          </div>

          {/* Interval (conditional) */}
          {(formData.frequency === 'HOURS_BASED' || formData.frequency === 'CONDITION_BASED') && (
            <div className={cn(PANEL, 'p-6 space-y-4')}>
              <h3 className="text-base font-semibold">Interval Settings</h3>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="intervalDays">Interval (Days)</Label>
                  <Input
                    id="intervalDays"
                    type="number"
                    placeholder="30"
                    value={formData.intervalDays}
                    onChange={(e) => setFormData({ ...formData, intervalDays: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="intervalHours">Interval (Hours)</Label>
                  <Input
                    id="intervalHours"
                    type="number"
                    placeholder="500"
                    value={formData.intervalHours}
                    onChange={(e) => setFormData({ ...formData, intervalHours: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Work Instructions */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Work Instructions</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="estimatedDuration">Estimated Duration (Hours)</Label>
              <Input
                id="estimatedDuration"
                type="number"
                step="0.5"
                placeholder="2.0"
                value={formData.estimatedDuration}
                onChange={(e) => setFormData({ ...formData, estimatedDuration: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="instructions">Instructions</Label>
              <Textarea
                id="instructions"
                placeholder="Maintenance instructions and procedures..."
                rows={4}
                value={formData.instructions}
                onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="checklist">Checklist Items (one per line)</Label>
              <Textarea
                id="checklist"
                placeholder={`Check oil level\nReplace filter\nInspect belts`}
                rows={5}
                value={formData.checklistItems}
                onChange={(e) => setFormData({ ...formData, checklistItems: e.target.value })}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/maintenance')}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !formData.name || !formData.equipmentId}>
              {saving ? 'Creating...' : 'Create Schedule'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
